import React, { useState } from 'react';
import Slider from 'react-slick';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import _ from 'lodash';

import AccommodationIcon from '../../assets/icons/accomodation_icon.svg';
import ArrowRight from '../../assets/icons/arrow-right.svg';
import ClockIcon from '../../assets/icons/clock_icon.svg';
import DinningIcon from '../../assets/icons/dinning_icon.svg';
import LuggageIcon from '../../assets/icons/luggage_icon.svg';
import NextSlide from '../../assets/icons/next-slide.svg';
import PrevSlide from '../../assets/icons/previous-slide.svg';
import SceneraryIcon from '../../assets/icons/scenerary_icon.svg';

import useWindowDimensions from '../../hooks/useWindowDimensions'
import { onEnter } from '../../utilities/accessibility/onEnter';
import { stripHtml } from '../../utilities/content/stripHtmlString';
import { removeEscapeHTML } from '../../utilities/content/formatContent';

import '../../styles/scss/elements/slick.scss';
import '../../styles/scss/elements/slick-theme.scss';
import '../../styles/scss/elements/train-card.scss';

Modal.setAppElement('#ylg-ms__listings-details');

// COMPONENT AND STYLES TAKEN DIRECTLY FROM YLG AGENT PORTAL

const amenityTypes = {
  accommodations: 'accommodations',
  checkInTimes: 'checkInTimes',
  dining: 'dining',
  luggage: 'luggage',
  notableScenery: 'notableScenery',
};

const amenityIcons = {
  accommodations: AccommodationIcon,
  checkInTimes: ClockIcon,
  dining: DinningIcon,
  luggage: LuggageIcon,
  notableScenery: SceneraryIcon,
};

const amenityLabels = {
  accommodations: 'Accommodations',
  checkInTimes: 'Check-in<br/>Times',
  dining: 'Dining',
  luggage: 'Luggage<br/>Guidelines',
  notableScenery: 'Notable<br/>Scenery',
};

const Information = ({ content, type }) => (
  <div className="information__wrapper">
    <div className="information__header">
      <div dangerouslySetInnerHTML={{ __html: amenityLabels[type]}} />
      <img alt={amenityLabels[type]} src={amenityIcons[type]} />
    </div>
    <div className="information__description" dangerouslySetInnerHTML={{ __html: content }} />
  </div>
);

const TrainDetailContent = ({ amenities, description, images, link, title }) => {
  const [slider, setSlider] = useState();

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  const renderArrows = () => {
    return (
      <div className="slider-arrow">
        <div
          className="arrow-btn prev"
          onClick={() => slider.slickPrev()}
          onKeyDown={event => onEnter(event, slider.slickPrev)}
        >
          <img src={PrevSlide} alt="previous-slide" />
        </div>
        <div
          className="arrow-btn next"
          onClick={() => slider.slickNext()}
          onKeyDown={event => onEnter(event, slider.slickNext)}
        >
          <img src={NextSlide} alt="next-slide" />
        </div>
      </div>
    );
  };

  return (
    <div className="train-detail">
      <section className="train-detail__title">
        <h2>{title}</h2>
      </section>
      <section className="train-detail__description">
        <p>{description && removeEscapeHTML(description)}</p>
      </section>
      {!_.isEmpty(images) && (
        <section className="train-detail__slider">
          <Slider
            {...settings}
            className="accomodation-slider"
            ref={c => setSlider(c)}
          >
            {_.map(images, (image, index) => (
              <div key={`${index}`}>
                <img alt={image.description} src={image.path} />
              </div>
            ))}
          </Slider>
          {images.length > 1 && renderArrows()}
        </section>
      )}
      {Boolean(!_.isEmpty(amenities)) && (
        <section className="train-detail__amenites-information">
          <div className="other-information">
            <h3
              className="train-detail__amenites-information__title"
              aria-label="Amenites"
            >
              Amenities
            </h3>
            {Boolean(_.get(amenities, 'luggage')) && (
              <Information
                content={_.get(amenities, 'luggage')}
                type={amenityTypes.luggage}
              />
            )}
            {Boolean(_.get(amenities, 'checkIn')) && (
              <Information
                content={_.get(amenities, 'checkIn')}
                type={amenityTypes.checkInTimes}
              />
            )}
            {Boolean(_.get(amenities, 'dining')) && (
              <Information
                content={_.get(amenities, 'dining')}
                type={amenityTypes.dining}
              />
            )}
            {Boolean(_.get(amenities, 'accommodations')) && (
              <Information
                content={_.get(amenities, 'accommodations')}
                type={amenityTypes.accommodations}
              />
            )}
            {Boolean(_.get(amenities, 'notableScenery')) && (
              <Information
                content={_.get(amenities, 'notableScenery')}
                type={amenityTypes.notableScenery}
              />
            )}
          </div>
        </section>
      )}
      {Boolean(link) && (
        <div className="train-detail__external-link" >
          <a href={link}>
            {`Learn more about ${title}`}
          </a>
        </div>
      )}
    </div>
  );
};

const TrainCard = ({ amenities, description, images, link, thumbnail, title }) => {
  const { width } = useWindowDimensions()
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [windowOffset, setWindowOffset] = useState(0)

  function toggleModal() {
    if (isModalOpen) {
      document.body.setAttribute('style', '')
      window.scrollTo(0, windowOffset)
    } else {
      setWindowOffset(window.scrollY)
      document.body.setAttribute('style', `overflow: hidden;`)
    }
    setIsModalOpen(!isModalOpen);
  }

  function formatDescriptionForCard(cardDescription) {
    const description = stripHtml(cardDescription);
    if (description && description.length > 170) {
      return `${description.replace(/^(.{170}[^\s]*).*/, "$1")} ...`;
    }
    return description;
  }

  return (
    <div className="u-full-width u-flex u-flex-justify-center">
      <div
        className="u-flex u-flex-column train-card"
        aria-label={title}
        onClick={toggleModal}
        onKeyDown={event => onEnter(event, toggleModal)}
      >
        <div
          className="u-flex u-flex-align-end image-container"
          aria-label={_.get(thumbnail, 'description')}
          style={{ backgroundImage: `url(${_.get(thumbnail, 'path')})` }}
        >
          <div className="image-gradient" />
          <span>{title}</span>
        </div>
        <div className="u-flex u-flex-column u-flex-space-between u-flex-align-center description-container">
          <p>{formatDescriptionForCard(description)}</p>
          <span aria-label="Learn more" className="u-flex u-flex-align-center">
            Learn more
            <img src={ArrowRight} alt="left calendar icon" />
          </span>
        </div>
      </div>
      <Modal
        parentSelector={() => document.getElementById('ylg-ms__listings-details')}
        isOpen={isModalOpen}
        onRequestClose={toggleModal}
        style={{
          overlay: { backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 9999 },
          content: {
            width: width < 1024 ? '100%' : '90%',
            overflowX: 'hidden',
            margin: 'auto',
            maxWidth: 1280,
            zIndex: 9999,
          },
        }}
      >
        <div
          className="modal-close"
          role="button"
          onClick={toggleModal}
          onKeyDown={event => onEnter(event, toggleModal)}
          tabIndex="0"
        />
        <TrainDetailContent
          amenities={amenities}
          description={description}
          images={images}
          link={link}
          title={title}
        />
      </Modal>
    </div>
  );
};

TrainCard.propTypes = {
  amenities: PropTypes.object,
  description: PropTypes.string.isRequired,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      path: PropTypes.string,
    })
  ).isRequired,
  title: PropTypes.string.isRequired,
};

export default TrainCard;
